// ONE place that knows whether the backend is answering, so every repeating poller can
// stand down together.
//
// WHY THIS EXISTS (owner ask, 2026-07-28): "review feedback and notifications (and any
// other repeating poll checks) should always be gated on health so that we don't just
// sit spamming a broken server/connection."
//
// Before this, `useHeartbeat` was the only poller that reacted to failure — it backs off
// 3s -> 30s and escalates to 'offline'. Every OTHER poller kept its full cadence into a
// dead backend: the review-activity probe every 10s and the notification badge every 30s,
// each retrying forever, each failure costing a CORS preflight as well as the request
// itself. So the moment the backend became unreachable the client got LOUDER, not
// quieter, precisely when it could least afford to. The owner's capture shows exactly
// that: a wall of failed requests, still going.
//
// The heartbeat already computes the answer. What was missing was a way for anything
// outside React to READ it — `useHeartbeat` returns status into a component's state, and
// `reviewActivityPoller` is a module singleton with no component to read it from. This
// module is that seam: the heartbeat publishes, everything else subscribes.
//
// DESIGN RULE: the heartbeat is the SOLE writer. Nothing else may publish a status, or
// two pollers disagreeing about reachability would each suppress the other's recovery
// probe and the app would stay stood-down after the backend came back. `/health` is the
// cheap DB-free endpoint built for this (main.py: "pinged constantly, must stay cheap"),
// and it is the only probe allowed to run while we believe the backend is down.

// 'connected' | 'reconnecting' | 'offline'. Optimistic at boot for the same reason
// useHeartbeat is: the first ping has not resolved yet, and suppressing every poller
// until it does would make a cold-started backend look like a broken one.
let status = 'connected';

// Consecutive successful pings since the last non-connected status. Exists for the
// owner's "auto refresh on health recovery (2x?) + delay" — a surface that wants to
// reload itself on recovery can require the connection to hold for more than the single
// ping that flipped the status, so a flapping backend does not trigger a reload storm of
// its own. That would be this bug wearing a different hat.
let consecutiveOk = 0;

const listeners = new Set();

function emit() {
  const snapshot = { status, consecutiveOk };
  // Copy first: a listener that unsubscribes itself while we iterate (the poller does
  // exactly this on teardown) must not perturb the set mid-loop.
  [...listeners].forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      // A throwing subscriber must not stop the others from hearing about recovery —
      // "some pollers never resumed" is a silent, permanent failure and much worse
      // than one broken listener.
    }
  });
}

/**
 * Publish the heartbeat's verdict. **Only `useHeartbeat` may call this.**
 *
 * @param {'connected'|'reconnecting'|'offline'} next
 */
export function setConnectionStatus(next) {
  if (next !== 'connected' && next !== 'reconnecting' && next !== 'offline') return;
  const changed = next !== status;
  if (next === 'connected') {
    // Count successes even when the status was ALREADY connected — the counter measures
    // how long the connection has held, which is what a recovery consumer needs. Reset
    // on any non-connected status so "2 in a row" cannot be satisfied by two successes
    // either side of an outage.
    consecutiveOk += 1;
  } else {
    consecutiveOk = 0;
  }
  status = next;
  // Emit on every ping, not only on transitions: `consecutiveOk` advancing IS the event
  // a recovery consumer is waiting for, and it moves without the status changing.
  if (changed || next === 'connected') emit();
}

/** The last published status. */
export function getConnectionStatus() {
  return status;
}

/**
 * Should a repeating background poller run right now?
 *
 * Deliberately gated on 'connected' rather than on "not offline". 'reconnecting' means a
 * ping has ALREADY failed — that is the moment to go quiet, not to keep the full cadence
 * running for the two further failures it takes to reach 'offline'. The cost of being
 * eager is bounded and small: the heartbeat retries after 3s, so a single blip stands the
 * other pollers down for one tick, and they probe immediately on recovery rather than
 * waiting out their interval.
 *
 * This must NEVER gate the heartbeat itself — that is the probe that discovers recovery,
 * and gating it on its own output would latch the app offline forever.
 */
export function isBackendReachable() {
  return status === 'connected';
}

/** Consecutive successful pings since the last non-connected status. */
export function getConsecutiveOk() {
  return consecutiveOk;
}

/**
 * Subscribe to health changes. Returns an unsubscribe function.
 *
 * The listener receives `{status, consecutiveOk}` and is called on every ping that is
 * either a transition or a success, so a consumer can implement "resume once the
 * connection has held for N pings" without polling this module.
 */
export function subscribeConnectionHealth(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test seam — resets module state between tests. Not part of the app contract. */
export function _reset() {
  status = 'connected';
  consecutiveOk = 0;
  listeners.clear();
}

export default isBackendReachable;
